/**
 * Visualization Color Constants
 *
 * Non-brand colors used across charts, status indicators, severity badges,
 * and data visualizations. These preserve exact hex values from the current
 * implementation — this is a substitution refactor, NOT a recolor.
 *
 * For brand colors (Ink, Hi-Vis Orange, Signal Teal, Amber, Paper),
 * use MUI theme tokens directly (theme.palette.primary.main, etc.).
 */

// ─────────────────────────────────────────────────────────────────────────────
// STATUS INDICATORS
// ─────────────────────────────────────────────────────────────────────────────

export const STATUS = {
  /** Success / Good / Safe — Material Green 500 */
  success: '#4caf50',
  /** Success variant — Material Green 400 */
  successLight: '#66bb6a',

  /** Warning / Caution / Moderate — Material Orange 500 */
  warning: '#ff9800',
  /** Warning variant — Material Orange 400 */
  warningLight: '#ffa726',

  /** Error / Danger / Critical — Material Red 500 */
  error: '#f44336',
  /** Error variant — Material Red 400 */
  errorLight: '#ef5350',

  /** Info / Neutral — Material Blue 500 */
  info: '#2196f3',
  /** Info variant — Material Blue 300 */
  infoLight: '#42a5f5',

  /** Inactive / Disabled / Neutral — Material Grey 500 */
  inactive: '#9e9e9e',

  /** Pure white (for text on dark backgrounds) */
  white: '#fff',
  /** Pure black (for text on light backgrounds) */
  black: '#000',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// SEVERITY INDICATORS (Risk / Ergonomic / Safety)
// ─────────────────────────────────────────────────────────────────────────────

export const SEVERITY = {
  /** Low risk / safe */
  low: '#4caf50',
  /** Medium risk / caution */
  medium: '#ff9800',
  /** High risk / danger */
  high: '#f44336',
  /** Critical risk / immediate action */
  critical: '#d32f2f',
  /** Very high risk (darker red) */
  veryHigh: '#b71c1c',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// CHART SERIES COLORS (Multi-line charts, stacked bars, etc.)
// ─────────────────────────────────────────────────────────────────────────────

export const CHART_SERIES = [
  '#2196f3',  // Blue
  '#4caf50',  // Green
  '#ff9800',  // Orange
  '#f44336',  // Red
  '#9c27b0',  // Purple
  '#00bcd4',  // Cyan
  '#ffeb3b',  // Yellow
  '#795548',  // Brown
  '#607d8b',  // Blue Grey
  '#e91e63',  // Pink
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// ACTIVITY TYPE COLORS (Motion detection / activity classification)
// ─────────────────────────────────────────────────────────────────────────────

export const ACTIVITY = {
  // Low risk activities (green shades)
  walking: '#4caf50',
  walkingLight: '#66bb6a',
  running: '#66bb6a',
  standing: '#2196f3',
  standingLight: '#64b5f6',
  sitting: '#64b5f6',

  // Medium risk activities (orange/amber)
  lifting: '#ff9800',
  liftingLight: '#ffa726',
  carrying: '#f44336',

  // High risk activities (purple/red shades)
  bending: '#9c27b0',
  bendingLight: '#ab47bc',
  pushing: '#673ab7',
  pulling: '#7b1fa2',
  twisting: '#ec407a',
  reaching: '#ba68c8',
  squatting: '#e91e63',
  kneeling: '#7e57c2',
  climbing: '#5c6bc0',
  /** Stairs down; stairs up uses `climbing`. */
  stairsDown: '#3949ab',
  crawling: '#26a69a',
  jumping: '#ff5722',

  // Neutral/other
  unknown: '#9e9e9e',
  idle: '#e0e0e0',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// PRODUCTION / THROUGHPUT STATUS
// ─────────────────────────────────────────────────────────────────────────────

export const PRODUCTION_STATUS = {
  /** Exceeds target / optimal */
  excellent: '#4caf50',
  /** Meets target / good */
  good: '#66bb6a',
  /** Near target / acceptable */
  acceptable: '#ffca28',
  /** Below target / warning */
  warning: '#ff9800',
  /** Significantly below target / critical */
  critical: '#f44336',
  /** Maintenance / offline */
  offline: '#9e9e9e',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// TRAFFIC LIGHT / BOTTLENECK INDICATORS
// ─────────────────────────────────────────────────────────────────────────────

export const TRAFFIC_LIGHT = {
  /** Green — no bottleneck / flowing */
  green: '#4caf50',
  /** Yellow — mild congestion */
  yellow: '#ffca28',
  /** Yellow 700 — darker warning variant */
  yellow700: '#fbc02d',
  /** Orange — moderate bottleneck */
  orange: '#ff9800',
  /** Red — severe bottleneck */
  red: '#f44336',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// GRADIENTS / BACKGROUNDS
// ─────────────────────────────────────────────────────────────────────────────

export const NEUTRAL = {
  grey50: '#fafafa',
  grey100: '#f5f5f5',
  grey200: '#eeeeee',
  grey300: '#e0e0e0',
  grey400: '#bdbdbd',
  grey500: '#9e9e9e',
  grey600: '#757575',
  grey700: '#616161',
  grey777: '#777777',
  grey800: '#424242',
  grey808: '#808080',
  grey888: '#888888',
  grey900: '#212121',
  darkBg: '#1e1e1e',
  /** Dark background with subtle blue tint (for playback controls, overlays) */
  darkBlueTint: '#1e1e2e',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// RECHARTS / DATA VIZ SPECIFIC
// ─────────────────────────────────────────────────────────────────────────────

export const RECHARTS = {
  /** Primary line/bar color */
  primary: '#8884d8',
  /** Secondary line/bar color */
  secondary: '#82ca9d',
  /** Light blue for area fills */
  areaBlue: '#90caf9',
  /** Purple tint for specific series */
  purple: '#ce93d8',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// TYPE EXPORTS
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// --- batch3 additions ---
// ─────────────────────────────────────────────────────────────────────────────

export const MAP_UI = {
  /** Amber/orange 300 (hover/secondary emphasis) */
  amberLight: '#ffb74d',
  /** Material Amber A700 (warning variant) */
  amber: '#ffc107',
  /** Light indigo (editor grid/helper) */
  gridHelper: '#7c83fd',
  /** Zinc 300 (floor plane wireframe) */
  floorWireframe: '#d4d4d8',
} as const;

export const DARK_BG = {
  /** Very dark bg for map containers/canvas overlays */
  veryDark: '#0a0a0a',
  /** Dark bg for 3D scenes */
  dark: '#0d0d0d',
  /** GitHub dark bg (DXF import preview) */
  githubDark: '#0d1117',
  /** Near-black panel bg */
  panel: '#111',
  /** Dark bg for R3F canvas (subtle blue tint) */
  canvasBlueTint: '#1a1a2e',
} as const;

// --- batch4 additions ---
// ─────────────────────────────────────────────────────────────────────────────

export const PPE_COLORS = {
  /** Hard hat — yellow/gold */
  hardHat: '#ffd700',
  /** Safety vest — orange */
  safetyVest: '#ff6b00',
  /** Steel toe boots — brown */
  boots: '#795548',
  /** Steel toe cap — blue grey 600 */
  steelToeCap: '#546e7a',
  /** Gloves — grey */
  gloves: '#9e9e9e',
  /** Ear protection — cyan */
  earProtection: '#4fc3f7',
  /** Ear muff shell — blue grey 900 */
  earMuffShell: '#263238',
  /** Ear muff cushion — blue grey 700 */
  earMuffCushion: '#455a64',
  /** Model label chip — blue grey */
  modelChip: '#607d8b',
  /** General hazard beacon/zone */
  hazard: '#ff0000',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// VEHICLE COLORS (Forklift / equipment fallback meshes)
// ─────────────────────────────────────────────────────────────────────────────

export const VEHICLE = {
  /** Forklift fallback body — amber 600 */
  forkliftFallback: '#f9a825',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// A11Y / ACCESSIBILITY
// ─────────────────────────────────────────────────────────────────────────────

export const A11Y = {
  /** Skip-to-content link background — blue 800 */
  skipToContentBg: '#1565c0',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// ZONE TYPE COLORS (Floor plan editor zone classification)
// ─────────────────────────────────────────────────────────────────────────────

export const ZONE = {
  /** General zone — blue 300 */
  general: '#64b5f6',
  /** Restricted zone — red 200 */
  restricted: '#ef9a9a',
  /** Hazard zone — amber/orange 300 */
  hazard: '#ffb74d',
  /** Safe zone — green 300 */
  safe: '#81c784',
  /** Workspace zone — purple 200 */
  workspace: '#ce93d8',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// SUMMARY STATISTICS CHART (exact legacy Recharts demo palette — fidelity)
// ─────────────────────────────────────────────────────────────────────────────

export const SUMMARY_CHART = {
  /** Bar series fills (exact prior values) */
  bars: {
    time: '#8884d8',
    alerts: '#FF6B6B',
    workers: '#00C49F',
    entries: '#FFBB28',
  },
  /** Pie cell palette (exact prior values) */
  pie: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD'],
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// --- finisher additions ---
// ─────────────────────────────────────────────────────────────────────────────

export const DXF_COLORS = {
  /** DXF geometry stroke (blue) */
  stroke: '#4a9eff',
  /** DXF geometry layer stroke (dark grey) */
  layerStroke: '#222',
  /** DXF background (white) */
  background: '#ffffff',
} as const;

export const GRADIENTS = {
  /** Auth/marketing page background (login, signup, pricing, accept-invite, legal pages) */
  authBackground: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  indigo: '#667eea',
  violet: '#764ba2',
  /** Blue button gradient (AcceptInvite submit) */
  blueButton: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
  /** Material Blue A200 (for gradient end) */
  blueA200: '#21CBF3',
} as const;

export const CHART_EXTRA = {
  indigo: '#7c83fd',
  teal: '#59b9a8',
  orange: '#f0833a',
  axisGrey: '#aaaaaa',
} as const;

export const RISK_LEVEL = {
  critical: '#ef4444',
  high: '#f59e0b',
  medium: '#eab308',
} as const;

export const COMPARE_PALETTE = ['#4fc3f7', '#aed581', '#ff8a65', '#f48fb1'] as const;

// ─────────────────────────────────────────────────────────────────────────────
// TYPE EXPORTS
// ─────────────────────────────────────────────────────────────────────────────

export type StatusColor = typeof STATUS[keyof typeof STATUS];
export type SeverityColor = typeof SEVERITY[keyof typeof SEVERITY];
export type ChartSeriesColor = typeof CHART_SERIES[number];
export type ActivityColor = typeof ACTIVITY[keyof typeof ACTIVITY];
